import React, {
    ChangeEvent,
    Key,
    MouseEventHandler,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState
} from 'react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import clsx from 'clsx'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TablePagination, { LabelDisplayedRowsArgs } from '@material-ui/core/TablePagination'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import Checkbox from '@material-ui/core/Checkbox'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { Theme } from '@material-ui/core/styles'
import { OnSortColumnClickFn, TableHead } from './TableHead'

import { defaultSortingSequence, SortDirection, SorterFn, useSort, useSortReturn } from '../../../Hooks/useSort'
import { useSelection, useSelectionReturn } from '../../../Hooks/useSelection'
import {
    DeleteAddToolbar,
    DeleteAddToolbarCustomAction,
    DeleteAddToolbarLocaleText,
    DeleteAddToolbarLocaleTextDefaults
} from './DeleteAddToolbar'
import { IconName } from '../../../../React/Common/Components/Icon'
import { DataTableRowLoader } from './DataTableRowLoader'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { usePermissions, UserPermissions } from '../../../Hooks/usePermissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { usePagination, usePaginationReturn } from '../../../Hooks/usePagination'
import { LanguageContext, PromptContext } from '../../../../React/Common/Contexts'
import { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageDataTable } from '../../../../../i18n'
import { COMMA, SPACE } from '../../../../NonReact/constants/Separators'

const RD_TABLE_ROW_CLASS = 'rd-table-row'
const ROUTER_DATATABLE_CLASS = 'router-datatable'
const ROUTER_DATATABLE_BODY_DATA_CELL_CLASS = 'router-datatable-body-data-cell'

interface DataTableLocaleText extends DeleteAddToolbarLocaleText {
    noRows: string
}

const DataTableLocaleTextDefaults: DataTableLocaleText = {
    ...DeleteAddToolbarLocaleTextDefaults,
    noRows: 'Nothing to show here'
}

/**
 * Page-specific toolbar action shown when rows are selected (in addition to generic Change/View/Delete).
 *
 * @typeParam T - Row type (same as the table row model).
 */
export type DataTableToolbarCustomAction<T> = {
    label: string
    icon?: IconName
    /**
     * Return true to show the control for the current selection.
     *
     * @param itemsSelectedCount - Number of selected rows.
     * @param selectedItems - Selected row models, for actions that only apply to rows in a particular state.
     */
    isVisible: (itemsSelectedCount: number, selectedItems: T[]) => boolean
    /**
     * When true (default), the user must have Update on `permissionName` for the action to show.
     * Set to false when the action is not gated on Update.
     */
    requiresUpdatePermission?: boolean
    /**
     * Invoked with the current selection when the user activates the control.
     *
     * @param selectedItems - Selected row models (same order as the table selection).
     */
    onClick: (selectedItems: T[]) => void
}

export enum TABLE_SIZE {
    MEDIUM = 'medium',
    SMALL = 'small'
}

export interface DataTableColumn<T, K extends keyof T> {
    /**
     * The object property that will be rendered in each cell.
     *
     * For example if we have data like this [{name: "yanis"}]
     * and we want to show the name
     * we need to set the field to "name" in the columns [{field:"name"}].
     *
     * "renderCell" will take precedence over "field".
     */
    field: K
    /**
     * React key, has to be unique.
     */
    key: string
    /**
     * Name that is going to be displayed on the head column.
     */
    headerName?: string
    /**
     * Function used to render the data in the table cell.
     */
    renderCell?: (row: T, rowIndex: number, column: DataTableColumn<T, K>, key: string | number) => ReactNode
    /**
     * Function used to sort data from this column.
     */
    sortFunction?: SorterFn<T>
    /**
     * Disables sorting for the column.
     */
    sortable?: boolean
    /**
     * Stops cell click event propagation.
     * Useful when "renderCell" renders some interactable element and we don't want it to trigger a row selection.
     */
    disableRowSelection?: boolean
    /**
     * Omit this column below the given breakpoint so the table stays usable on narrow viewports
     * (e.g. TelSys list keeps ID + description on phones; wider screens show full set).
     */
    hideBelow?: ResponsiveBreakpoint
    /**
     * Cell alignment. Defaults to 'left' when omitted.
     */
    align?: 'left' | 'center' | 'right'
    /**
     * When {@link DataTableSortProps.sortServer} is enabled, use this token in the {@link DataTableSortProps.onSortChange}
     * payload instead of {@link field} (for example UI column {@code LockOutDisplay} vs API {@code LockOut}).
     */
    serverSortField?: string
}

type ResponsiveBreakpoint = 'sm' | 'md'

const DATA_TABLE_BREAKPOINT_SM: ResponsiveBreakpoint = 'sm'
const DATA_TABLE_BREAKPOINT_MD: ResponsiveBreakpoint = 'md'

type DataTablePaginationProps = {
    /**
     * Disables pagination controls.
     */
    paginationDisabled?: boolean
    /**
     * If set to true it won't do pagination work, the pagination needs to be handled outside using the pagination events.
     * When set to false the data is divided into pages internally.
     * This is useful when we fetch too much data upfront and don't want to display it all in one page.
     */
    paginationServer?: boolean
    /**
     * Options that show on the "Rows per page"
     *
     * @default [25, 50, 100]
     */
    defaultRowsPerPageOptions?: Array<number | { value: number; label: string }>
    /**
     * Default rows per page.
     * If defaultRowsPerPageOptions is set it should match one of its values
     */
    defaultRowsPerPage?: number
    /**
     * Default total rows count.
     * Sometimes it can be expensive to know the total, in that case set it to -1.
     * When working with token/time server based pagination set it to -1.
     */
    defaultRowsCount?: number
    /**
     * Default page number.
     * Use it to control page number from outside
     */
    defaultPageNumber?: number
    /**
     * Callback fired when the number current page is changed.
     */
    onPageChange?: (newPage: number, rowsPerPage: number) => void
    /**
     * Callback fired when the number of rows per page is changed.
     */
    onRowsPerPageChange?: (currentPage: number, newRowsPerPage: number) => void
}

type DataTableSelectionProps = {
    isSelectable?: boolean
    preserveSelectionOnDataChange?: boolean
}

type DataTableSortProps = {
    /**
     * If set to true it won't do sorting work, sorting needs to be handled outside using the sorting events.
     * If set to false sorting will be done internally.
     */
    sortServer?: boolean
    /**
     * Callback fired when a sorting event is triggered.
     */
    onSortChange?: (sortingString: Definable<string>) => void
}

/**
 * A group of rows rendered under a collapsible section-header row inside the table body.
 * When the `sections` prop is supplied on {@link DataTable}, rows are sourced from these
 * sections rather than from the `data` prop.
 *
 * @typeParam T - Row type (same as the table row model).
 */
export type DataTableSection<T> = {
    /** Stable unique key for this section. */
    key: string
    /** Text shown in the clickable section-header row. */
    title: string
    /** Rows belonging to this section, shown when the section is expanded. */
    rows: readonly T[]
    /** When true the section starts expanded. Defaults to false (collapsed). */
    defaultExpanded?: boolean
}

/**
 * Opt-out checkbox offered in the delete-confirmation prompt.
 */
export type DataTableDeleteConfirmationCheckbox = {
    /** Text shown beside the checkbox. */
    label: string
    /** State the checkbox starts in; the classic admin lists start these ticked. */
    defaultChecked: boolean
}

/**
 * Row type constraint for {@link DataTable}.
 * Row types need either a {@code Description} field or {@link DataTableProps.getRowDescription} for
 * delete-confirmation labels (see {@link handleToolbarDeleteClick}).
 */
export type DataTableProps<T extends { key: Key }, K extends keyof T> = {
    columns: DataTableColumn<T, K>[]
    /**
     * <p>Resolves the label shown in the delete-confirmation prompt for a row.</p>
     *
     * <p>Defaults to {@code row.Description} when present; otherwise falls back to {@code String(row.key)}.</p>
     */
    getRowDescription?: (row: T) => string
    /**
     * Replaces the default delete-confirmation message for the current selection.
     *
     * Supply this where deleting cascades beyond the selected rows and the administrator has to be told what
     * else goes with them, in place of the generic prompt listing only the row descriptions.
     *
     * @param selectedItems - Rows that were ticked when Delete was pressed.
     */
    getDeleteConfirmationMessage?: (selectedItems: T[]) => string
    /**
     * Adds an opt-out checkbox to the delete-confirmation prompt for the current selection.
     *
     * Return undefined for a selection needing no checkbox. The state the user leaves it in is handed to
     * {@link DataTableProps.onToolbarDeleteClick}, so a page can offer to delete the row without its
     * dependants the way the classic admin lists do.
     *
     * @param selectedItems - Rows that were ticked when Delete was pressed.
     */
    getDeleteConfirmationCheckbox?: (selectedItems: T[]) => Definable<DataTableDeleteConfirmationCheckbox>
    /**
     * Decides whether the current selection may be deleted at all.
     *
     * Returning false disables the Delete button rather than letting the user confirm an action that will be
     * refused — read-only rows, for example. The owning page should still refuse the operation itself, since
     * this only governs the button.
     *
     * @param selectedItems - Rows currently ticked.
     */
    canDeleteSelection?: (selectedItems: T[]) => boolean
    /**
     * Tooltip shown when {@link DataTableProps.canDeleteSelection} has disabled Delete.
     */
    toolbarDeleteDisabledTitle?: string
    /**
     * <p>Resolves an extra CSS class applied to a body row, keyed off the row's data.</p>
     *
     * <p>Returns an empty string (or a falsy value) for rows needing no extra class. Used, for
     * example, to visually mark and disable soft-deleted (pending deletion) rows.</p>
     */
    getRowClassName?: (row: T) => string
    /**
     * Optional title row rendered inside the table header above the column labels.
     */
    headerTitle?: ReactNode
    /**
     * <p>Groups of rows rendered under collapsible section-header rows in the table body.</p>
     *
     * <p>When provided, {@link data} is ignored. Sorting and pagination do not apply in
     * sections mode — all rows are rendered inline under their respective headers.</p>
     */
    sections?: readonly DataTableSection<T>[]
    permissionName?: AdminBackendPermissionNames
    data?: T[]
    totalCount?: number
    colIndexToTriggerClick?: number
    /**
     * Translations
     */
    localeText?: Partial<DataTableLocaleText>
    /**
     * Table is in loading state.
     */
    isLoading?: boolean
    /**
     * Number of rows to show in the loading skeleton.
     */
    loadingSkeletonNumberOfRows?: number
    /**
     * Removes most of the padding from the table
     */
    dense?: boolean
    /**
     * Display delete-add toolbar.
     */
    toolbarDisabled?: boolean
    /**
     * When true, the delete toolbar action is not shown (add/change/view still follow permissions).
     */
    toolbarHideDelete?: boolean
    /**
     * Callback fired when a toolbar add event is triggered.
     */
    onToolbarAddClick?: () => void
    /**
     * Optional bulk insert/upload wizard entry point (footer toolbar, Create permission).
     */
    onToolbarBulkAddClick?: () => void
    /**
     * When true, the Add button is disabled (e.g. when filters required for add are not set).
     */
    toolbarAddDisabled?: boolean
    /**
     * Tooltip shown when the Add button is disabled (on hover or focus).
     */
    toolbarAddDisabledTitle?: string
    /** Disables the optional Bulk Add toolbar control when set. */
    toolbarBulkAddDisabled?: boolean
    /** Tooltip when Bulk Add is disabled. */
    toolbarBulkAddDisabledTitle?: string
    /**
     * Callback fired when a toolbar change event is triggered.
     */
    onToolbarChangeClick?: (selectedItems: T[]) => void
    /**
     * Callback fired when a toolbar view event is triggered.
     */
    onToolbarViewClick?: (selectedItems: T[]) => void
    /**
     * Callback fired when a toolbar delete event is confirmed.
     *
     * @param selectedItems - Rows that were ticked.
     * @param isConfirmationChecked - State of the prompt checkbox, or undefined when the selection had none.
     */
    onToolbarDeleteClick?: (selectedItems: T[], isConfirmationChecked?: boolean) => void
    /**
     * Optional check run before the delete confirmation prompt is shown.
     *
     * Return false to abandon the delete, having told the user why. Use it where a selection can be invalid in a
     * way the toolbar cannot express, so the refusal arrives before the user is asked to confirm rather than
     * after. Deletes are allowed when it is not supplied.
     *
     * @param selectedItems - Selected row models.
     */
    canToolbarDelete?: (selectedItems: T[]) => boolean
    /**
     * Extra selection toolbar actions (labels/icons/visibility supplied by the page).
     */
    toolbarCustomActions?: DataTableToolbarCustomAction<T>[]
    /**
     * <p>Optional content rendered in the table footer row alongside pagination (and the delete/add toolbar when it is shown).</p>
     *
     * <p>Use for page-specific controls that should sit at the bottom of the table like other admin lists, without adding feature logic to {@link DataTable}.</p>
     */
    toolbarFooterSlot?: ReactNode
    /**
     * Notifies parent whenever the set of selected rows changes (checkbox selection).
     */
    onSelectionChange?: (selectedItems: T[]) => void
    /**
     * When set, receives imperative selection helpers aligned with the table's internal checkbox state.
     */
    selectionBridgeRef?: React.MutableRefObject<OnlyNullable<DataTableSelectionBridge>>
} & DataTablePaginationProps &
    DataTableSelectionProps &
    DataTableSortProps

export type DataTableSortDirection = SortDirection

// Stable empty array used as the default for the optional `data` prop so that
// useSort's data-change effect does not fire on every render when no data is passed.
const EMPTY_TABLE_DATA: never[] = []

/**
 * Imperative hooks for pages that render extra controls outside {@link DataTable} but need the same
 * row selection state (for example "select all on page" / "clear selection" toolbar buttons).
 */
export type DataTableSelectionBridge = {
    selectAllCurrentPage: () => void
    clearSelection: () => void
}

export function DataTable<T extends { key: Key }, K extends keyof T>({
    columns,
    getRowDescription,
    getDeleteConfirmationMessage,
    getDeleteConfirmationCheckbox,
    canDeleteSelection,
    toolbarDeleteDisabledTitle,
    getRowClassName,
    headerTitle,
    sections,
    permissionName,
    colIndexToTriggerClick,
    data = EMPTY_TABLE_DATA,
    localeText,
    isLoading,
    dense,
    toolbarDisabled,
    toolbarHideDelete,
    paginationDisabled,
    paginationServer,
    defaultRowsPerPageOptions,
    defaultRowsPerPage,
    defaultRowsCount,
    defaultPageNumber,
    onPageChange,
    onRowsPerPageChange,
    isSelectable,
    preserveSelectionOnDataChange,
    sortServer,
    onSortChange,
    onToolbarDeleteClick,
    canToolbarDelete,
    onToolbarAddClick,
    onToolbarBulkAddClick,
    onToolbarChangeClick,
    onToolbarViewClick,
    toolbarCustomActions,
    toolbarFooterSlot,
    onSelectionChange,
    toolbarAddDisabled,
    toolbarAddDisabledTitle,
    toolbarBulkAddDisabled,
    toolbarBulkAddDisabledTitle,
    totalCount,
    selectionBridgeRef,
    loadingSkeletonNumberOfRows = 5
}: DataTableProps<T, K>): ReactElement {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageDataTable.NAME)

    const theme: Theme = useTheme<Theme>()
    const isBelowSm: boolean = useMediaQuery(theme.breakpoints.down(DATA_TABLE_BREAKPOINT_SM))
    const isBelowMd: boolean = useMediaQuery(theme.breakpoints.down(DATA_TABLE_BREAKPOINT_MD))

    const visibleColumns: DataTableColumn<T, K>[] = useMemo(
        (): DataTableColumn<T, K>[] =>
            columns.filter((col: DataTableColumn<T, K>): boolean => {
                if (col.hideBelow === DATA_TABLE_BREAKPOINT_SM) return !isBelowSm
                if (col.hideBelow === DATA_TABLE_BREAKPOINT_MD) return !isBelowMd
                return true
            }),
        [columns, isBelowSm, isBelowMd]
    )

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const lang: DataTableLocaleText = useMemo(
        (): DataTableLocaleText => ({ ...DataTableLocaleTextDefaults, ...localeText }),
        [localeText]
    )

    const userPermissions: UserPermissions = usePermissions(permissionName)

    const [sortedData, sort, sortDirection, sortOrderBy, nextSortDirection]: useSortReturn<T, K> = useSort(
        data,
        !!sortServer
    )

    const [pageNumber, getPage, rowsPerPage, changeRowsPerPage]: usePaginationReturn<T> = usePagination(
        sortedData,
        !paginationDisabled,
        defaultPageNumber ?? 0,
        defaultRowsPerPage || 25,
        !paginationServer
    )

    const currentPageRows: T[] = useMemo((): T[] => getPage(pageNumber), [getPage, pageNumber])

    const [
        selectionCount,
        selectionState,
        isSelected,
        selectionToggleOne,
        selectionToggleMulti,
        selectionToggleAll,
        reset
    ]: useSelectionReturn<T> = useSelection(getPage(pageNumber), isSelectable || false, preserveSelectionOnDataChange)

    const selectedItems: T[] = useMemo(
        (): T[] =>
            data.reduce((acc: T[], item: T): T[] => {
                if (isSelected(item.key)) {
                    acc.push(item)
                }

                return acc
            }, []),
        [data, isSelected]
    )

    useLayoutEffect((): Voidable<() => void> => {
        if (!selectionBridgeRef) {
            return (): void => {}
        }

        if (!isSelectable) {
            selectionBridgeRef.current = null

            return (): void => {
                selectionBridgeRef.current = null
            }
        }

        selectionBridgeRef.current = {
            selectAllCurrentPage: (): void => {
                selectionToggleAll(getPage(pageNumber))
            },
            clearSelection: (): void => {
                reset()
            }
        }

        return (): void => {
            selectionBridgeRef.current = null
        }
    }, [isSelectable, selectionBridgeRef, selectionToggleAll, getPage, pageNumber, reset])

    useEffect((): void => {
        if (!isSelectable) return

        onSelectionChange?.(selectedItems)
    }, [isSelectable, onSelectionChange, selectedItems])

    const handleColumnSortClick: OnSortColumnClickFn<T, K> = useCallback(
        (column: DataTableColumn<T, K>): void => {
            const nextDirection: DataTableSortDirection =
                column.field === sortOrderBy ? nextSortDirection() : defaultSortingSequence[0]

            sort(column.sortFunction, column.field, nextDirection)
            if (onSortChange) {
                const sortFieldToken: string =
                    sortServer && isStringNotEmpty(column.serverSortField)
                        ? column.serverSortField
                        : column.field?.toString() ?? EMPTY_STRING

                onSortChange(
                    IsVarEmpty(nextDirection) || IsVarEmpty(column.field)
                        ? undefined
                        : `${sortFieldToken}:${nextDirection}`
                )
            }
        },
        [nextSortDirection, onSortChange, sort, sortOrderBy, sortServer]
    )

    const handleSelectAllClick: () => void = useCallback((): void => {
        if (!isSelectable) return

        selectionToggleAll(currentPageRows)
    }, [currentPageRows, isSelectable, selectionToggleAll])

    const handleRowClick: (name: Key) => void = useCallback(
        (name: Key): void => {
            if (!isSelectable) return

            selectionToggleOne(name, currentPageRows)
        },
        [currentPageRows, isSelectable, selectionToggleOne]
    )

    const handleShiftRowClick: (key: Key) => void = useCallback(
        (key: Key): void => {
            if (!isSelectable) return

            // Find previous selected item
            const indexOfClicked: number = currentPageRows.findIndex(
                (currentItem: T): boolean => currentItem.key === key
            )

            if (indexOfClicked === -1) {
                selectionToggleOne(key, currentPageRows)

                return
            }

            // Get items up to the clicked item
            const itemsUptoClicked: T[] = currentPageRows.slice(0, indexOfClicked + 1)

            // Find the last selected item before the clicked one
            const lastSelectedIndex: number = itemsUptoClicked.reduce(
                (lastIndex: number, item: T, index: number): number => (isSelected(item.key) ? index : lastIndex),
                -1
            )

            if (lastSelectedIndex === -1) {
                selectionToggleOne(key, currentPageRows)

                return
            }

            // Get items between last selected and clicked item
            const itemsToSelect: T[] = itemsUptoClicked.slice(lastSelectedIndex + 1)
            const keysOfItemsToSelect: Key[] = itemsToSelect.map((item: T): Key => item.key)

            selectionToggleMulti(keysOfItemsToSelect, currentPageRows)
        },
        [currentPageRows, isSelectable, selectionToggleOne, selectionToggleMulti]
    )

    const handleToolbarAddClick: MouseEventHandler<HTMLButtonElement> = useCallback((): void => {
        onToolbarAddClick?.()
    }, [onToolbarAddClick])

    const handleToolbarBulkAddClick: MouseEventHandler<HTMLButtonElement> = useCallback((): void => {
        onToolbarBulkAddClick?.()
    }, [onToolbarBulkAddClick])

    const handleClickWithSelectedOptions = useCallback(
        (func?: (selectedItems: T[]) => void): void => {
            func?.(selectedItems)
        },
        [selectedItems]
    )

    const handleToolbarViewClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (): void => handleClickWithSelectedOptions(onToolbarViewClick),
        [onToolbarViewClick, data, isSelected]
    )

    const handleToolbarChangeClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (): void => handleClickWithSelectedOptions(onToolbarChangeClick),
        [onToolbarChangeClick, data, isSelected]
    )

    const resolvedToolbarCustomActions: DeleteAddToolbarCustomAction[] = useMemo((): DeleteAddToolbarCustomAction[] => {
        if (!toolbarCustomActions?.length) return []

        return toolbarCustomActions.map(
            (action: DataTableToolbarCustomAction<T>): DeleteAddToolbarCustomAction => ({
                label: action.label,
                icon: action.icon,
                isVisible: (count: number): boolean => action.isVisible(count, selectedItems),
                requiresUpdatePermission: action.requiresUpdatePermission,
                onClick: (): void => {
                    action.onClick(selectedItems)
                }
            })
        )
    }, [toolbarCustomActions, selectedItems])

    const resolveRowDescription = useCallback(
        (row: T): string => {
            if (isVarNotEmpty(getRowDescription)) {
                return getRowDescription(row)
            }

            const rowWithDescription: T & { Description?: string } = row

            return isStringNotEmpty(rowWithDescription.Description)
                ? (rowWithDescription.Description)
                : String(row.key)
        },
        [getRowDescription]
    )

    const handleToolbarDeleteClick: MouseEventHandler<HTMLButtonElement> = useCallback((): void => {
        // Asked before the prompt: a page that refuses this selection should say so instead of first asking the
        // user to confirm an action it was never going to carry out.
        if (isVarNotEmpty(canToolbarDelete) && !canToolbarDelete(selectedItems)) return

        const confirmationMessage: string = isVarNotEmpty(getDeleteConfirmationMessage)
            ? getDeleteConfirmationMessage(selectedItems)
            : language.getInterpolated(
                  LanguageDataTable.DataTable_AreYouSure,
                  selectedItems.map(resolveRowDescription).join(`${COMMA}${SPACE}`)
              )

        const checkbox: Definable<DataTableDeleteConfirmationCheckbox> = isVarNotEmpty(getDeleteConfirmationCheckbox)
            ? getDeleteConfirmationCheckbox(selectedItems)
            : undefined

        displayPromptPromise(
            confirmationMessage,
            undefined,
            undefined,
            isVarNotEmpty(checkbox)
                ? { checkMessage: checkbox.label, defaultChecked: checkbox.defaultChecked }
                : undefined
        )
            .then((isConfirmationChecked: Definable<boolean>): void => {
                // The prompt resolves false when it carries no checkbox at all, so the resolved value is only
                // meaningful — and only forwarded — when one was actually offered.
                onToolbarDeleteClick?.(selectedItems, isVarNotEmpty(checkbox) ? isConfirmationChecked : undefined)
            })
            .catch((): void => {
                // Do nothing, user cancelled the prompt
            })
    }, [
        canToolbarDelete,
        displayPromptPromise,
        getDeleteConfirmationCheckbox,
        getDeleteConfirmationMessage,
        language,
        onToolbarDeleteClick,
        resolveRowDescription,
        selectedItems
    ])

    const isToolbarDeleteDisabled: boolean = useMemo(
        (): boolean => isVarNotEmpty(canDeleteSelection) && !canDeleteSelection(selectedItems),
        [canDeleteSelection, selectedItems]
    )

    const handleRowKeyClick = useCallback(
        (row: T): void => {
            reset()

            if (userPermissions?.Read && !userPermissions?.Update) {
                onToolbarViewClick?.([row])
            } else {
                onToolbarChangeClick?.([row])
            }
        },
        [onToolbarChangeClick, onToolbarViewClick, userPermissions]
    )

    const [expandedSections, setExpandedSections] = useState<ReadonlySet<string>>(
        (): ReadonlySet<string> =>
            new Set(
                (sections ?? [])
                    .filter((s: DataTableSection<T>): boolean => s.defaultExpanded === true)
                    .map((s: DataTableSection<T>): string => s.key)
            )
    )

    const toggleSection = useCallback((sectionKey: string): void => {
        setExpandedSections((current: ReadonlySet<string>): ReadonlySet<string> => {
            const next: Set<string> = new Set(current)

            if (next.has(sectionKey)) {
                next.delete(sectionKey)
            } else {
                next.add(sectionKey)
            }

            return next
        })
    }, [])

    const handlePageChange: (event: OnlyNullable<React.MouseEvent<HTMLButtonElement>>, newPage: number) => void =
        useCallback(
            (_: OnlyNullable<React.MouseEvent<HTMLButtonElement>>, newPage: number): void => {
                if (onPageChange) {
                    onPageChange(newPage, rowsPerPage)
                }

                getPage(newPage)
            },
            [getPage, onPageChange, rowsPerPage, paginationServer]
        )

    const handleChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const newValue: number = Number(event.target.value)

            if (onRowsPerPageChange) {
                onRowsPerPageChange(pageNumber, newValue)
            }

            changeRowsPerPage(newValue)

            if (!paginationServer) {
                getPage(0)
            }
        },
        [changeRowsPerPage, getPage, onRowsPerPageChange, pageNumber, paginationServer]
    )

    const renderTableBodyContent = (): ReactNode => {
        if (isLoading) {
            return (
                <DataTableRowLoader
                    noRows={rowsPerPage}
                    dataColumnCount={visibleColumns.length}
                    includeSelectionColumn={!!isSelectable}
                />
            )
        }

        const colSpan: number = (isSelectable ? 1 : 0) + visibleColumns.length

        if (sections) {
            if (sections.length === 0) {
                return (
                    <TableRow>
                        <TableCell colSpan={colSpan}>{lang.noRows}</TableCell>
                    </TableRow>
                )
            }

            return sections.map((section: DataTableSection<T>, sectionIndex: number): ReactNode => {
                const isExpanded: boolean = expandedSections.has(section.key)
                const isOdd: boolean = sectionIndex % 2 === 0

                return (
                    <React.Fragment key={section.key}>
                        <TableRow
                            className={clsx(
                                'rd-table-section-header',
                                isOdd ? 'rd-table-section-header--odd' : 'rd-table-section-header--even'
                            )}
                            onClick={(): void => toggleSection(section.key)}
                        >
                            <TableCell colSpan={colSpan} className='rd-table-section-header-cell'>
                                <div className='rd-table-section-header-content'>
                                    <span className='rd-table-section-header-title'>{section.title}</span>
                                    <ExpandMoreIcon
                                        className={clsx(
                                            'rd-table-section-header-icon',
                                            isExpanded && 'rd-table-section-header-icon--expanded'
                                        )}
                                    />
                                </div>
                            </TableCell>
                        </TableRow>
                        {isExpanded &&
                            section.rows.map(
                                (row: T, rowIdx: number): ReactNode => {
                                    const rowKey: Key = isVarNotEmpty(row.key) ? row.key : rowIdx

                                    return (
                                        <TableRow key={rowKey} className={RD_TABLE_ROW_CLASS}>
                                            {visibleColumns.map(
                                                (col: DataTableColumn<T, K>, idx: number): ReactNode => (
                                                    <TableCell
                                                        key={`${rowKey}:${col.key}`}
                                                        className={ROUTER_DATATABLE_BODY_DATA_CELL_CLASS}
                                                        id={
                                                            idx === 0
                                                                ? `section-${section.key}-row-${String(rowKey)}`
                                                                : undefined
                                                        }
                                                        align={col.align ?? 'left'}
                                                    >
                                                        {col.renderCell
                                                            ? col.renderCell(row, rowIdx, col, rowKey)
                                                            : row[col.field]}
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    )
                                }
                            )}
                    </React.Fragment>
                )
            })
        }

        if (currentPageRows.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan}>{lang.noRows}</TableCell>
                </TableRow>
            )
        }

        return currentPageRows.map((row: T, rowIdx: number): ReactNode => {
            const rowKey: Key = isVarNotEmpty(row.key) ? row.key : rowIdx
            const isItemSelected: boolean = isSelected(row.key)
            const labelId: string = `enhanced-table-checkbox-${rowIdx}`

            return (
                <TableRow
                    className={clsx(RD_TABLE_ROW_CLASS, getRowClassName ? getRowClassName(row) : undefined)}
                    hover
                    onClick={(event: React.MouseEvent): void => {
                        if (event.shiftKey) {
                            handleShiftRowClick(row.key)
                        } else {
                            handleRowClick(row.key)
                        }
                    }}
                    role='checkbox'
                    aria-checked={isItemSelected}
                    key={rowKey}
                    selected={isItemSelected}
                >
                    {isSelectable ? (
                        <TableCell
                            padding='checkbox'
                            key={`${rowKey}:selectable`}
                            className='router-datatable-body-selection-cell'
                        >
                            <Checkbox checked={isItemSelected} inputProps={{ 'aria-labelledby': labelId }} />
                        </TableCell>
                    ) : (
                        <></>
                    )}
                    {visibleColumns.map(
                        (col: DataTableColumn<T, K>, idx: number): ReactNode => (
                            <TableCell
                                key={`${rowKey}:${col.key}`}
                                className={clsx(
                                    ROUTER_DATATABLE_BODY_DATA_CELL_CLASS,
                                    isVarNotEmpty(colIndexToTriggerClick) && idx === colIndexToTriggerClick
                                        ? 'router-datatable-body-clickable'
                                        : EMPTY_STRING
                                )}
                                id={idx === 0 ? labelId : undefined}
                                align={col.align ?? 'left'}
                                onMouseDown={(): void =>
                                    isVarNotEmpty(colIndexToTriggerClick) && idx === colIndexToTriggerClick
                                        ? handleRowKeyClick(row)
                                        : undefined
                                }
                                onClick={
                                    col.disableRowSelection
                                        ? (e: React.MouseEvent): void => e.stopPropagation()
                                        : undefined
                                }
                            >
                                {col.renderCell ? col.renderCell(row, rowIdx, col, rowKey) : row[col.field]}
                            </TableCell>
                        )
                    )}
                </TableRow>
            )
        })
    }

    return (
        <div
            className={clsx(ROUTER_DATATABLE_CLASS, isBelowMd && 'router-datatable--viewport-below-md')}
            data-testid={ROUTER_DATATABLE_CLASS}
            data-viewport-below-md={isBelowMd ? 'true' : 'false'}
        >
            <TableContainer className='router-datatable-table-container'>
                <Table
                    className='rd-table'
                    aria-labelledby='tableTitle'
                    size={dense ? TABLE_SIZE.SMALL : TABLE_SIZE.MEDIUM}
                    aria-label='data table'
                >
                    <TableHead
                        title={headerTitle}
                        isSelectable={isSelectable || false}
                        sortDirection={sortDirection}
                        sortOrderBy={sortOrderBy}
                        onSelectAllClick={handleSelectAllClick}
                        onSortColumnClick={handleColumnSortClick}
                        columns={visibleColumns}
                        selectionState={selectionState}
                    />
                    <TableBody className='router-datatable-body'>{renderTableBodyContent()}</TableBody>
                </Table>
            </TableContainer>
            <div className='router-datatable-toolbar-container'>
                {isVarNotEmpty(toolbarFooterSlot) ? (
                    <div className='router-datatable-footer-slot'>{toolbarFooterSlot}</div>
                ) : null}
                {!toolbarDisabled ? (
                    <DeleteAddToolbar
                        itemsSelectedCount={selectionCount}
                        onAddClick={handleToolbarAddClick}
                        onBulkAddClick={onToolbarBulkAddClick ? handleToolbarBulkAddClick : undefined}
                        onChangeClick={handleToolbarChangeClick}
                        onDeleteClick={toolbarHideDelete ? undefined : handleToolbarDeleteClick}
                        deleteButtonDisabled={isToolbarDeleteDisabled}
                        deleteButtonDisabledTitle={toolbarDeleteDisabledTitle}
                        onViewClick={handleToolbarViewClick}
                        customActions={resolvedToolbarCustomActions}
                        localeText={lang}
                        permissionName={permissionName}
                        addButtonDisabled={toolbarAddDisabled}
                        addButtonDisabledTitle={toolbarAddDisabledTitle}
                        bulkAddButtonDisabled={toolbarBulkAddDisabled}
                        bulkAddButtonDisabledTitle={toolbarBulkAddDisabledTitle}
                    />
                ) : (
                    <></>
                )}
                {!paginationDisabled ? (
                    <TablePagination
                        rowsPerPageOptions={defaultRowsPerPageOptions || [25, 50, 100]}
                        component='div'
                        count={paginationServer ? totalCount ?? -1 : defaultRowsCount || data.length}
                        rowsPerPage={rowsPerPage}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={language.get(LanguageDataTable.DataTable_RowsPerPage)}
                        labelDisplayedRows={({ from, to, count }: LabelDisplayedRowsArgs): string =>
                            count < 0
                                ? language.getInterpolated(LanguageDataTable.DataTable_OfMoreThan, from, to, to)
                                : language.getInterpolated(LanguageDataTable.DataTable_Of, from, to, count)
                        }
                        nextIconButtonText={language.get(LanguageDataTable.DataTable_Next)}
                        backIconButtonText={language.get(LanguageDataTable.DataTable_Previous)}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
